import { X } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { ICON_STROKE } from "~/lib/icon";

export type VerificationButtonSize = "sm" | "md";

/** Centred sheet title; the close control sits top right when given. */
export function SheetHeader({
  id,
  title,
  onClose,
}: {
  id: string;
  title: string;
  onClose?: () => void;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "44px 1fr 44px",
        alignItems: "start",
        gap: "var(--chat-space-2)",
        margin: "0 calc(-1 * var(--chat-space-3))",
      }}
    >
      <span aria-hidden />
      <h2
        id={id}
        style={{
          margin: 0,
          paddingTop: "var(--chat-space-2)",
          textAlign: "center",
          textWrap: "balance",
          color: "var(--chat-fg-primary)",
          fontFamily: "var(--chat-font-display)",
          fontSize: "var(--chat-type-display-lg-size)",
          lineHeight: "var(--chat-type-display-lg-line)",
          fontWeight: 500,
        }}
      >
        {title}
      </h2>
      {onClose ? (
        <button
          type="button"
          className="chat-button"
          aria-label="Close"
          onClick={onClose}
          style={{
            width: 44,
            height: 44,
            margin: "calc(-1 * var(--chat-space-1)) 0 0",
            borderRadius: "var(--chat-radius-md)",
            border: "none",
            background: "transparent",
            color: "var(--chat-fg-secondary)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <X size={18} strokeWidth={ICON_STROKE} />
        </button>
      ) : (
        <span aria-hidden />
      )}
    </div>
  );
}

export function VerificationStatusText({
  children,
  danger,
}: {
  children: ReactNode;
  danger?: boolean;
}) {
  return (
    <p
      role="status"
      style={{
        margin: 0,
        color: danger ? "var(--chat-state-danger)" : "var(--chat-fg-muted)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-sm-line)",
        textAlign: "center",
      }}
    >
      {children}
    </p>
  );
}

export function VerificationPrimaryButton({
  children,
  onClick,
  disabled,
  height = 32,
  size = "sm",
  type = "button",
  block = false,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  height?: number;
  size?: VerificationButtonSize;
  type?: "button" | "submit";
  /** Fill the container's width (stacked dialog actions). */
  block?: boolean;
}) {
  const sizeStyle: CSSProperties =
    size === "md"
      ? {
          ...(block ? { width: "100%" } : { minWidth: 280 }),
          minHeight: 44,
          padding: "0 var(--chat-space-5)",
          whiteSpace: "nowrap",
        }
      : { height, padding: "0 var(--chat-space-4)", ...(block ? { width: "100%" } : {}) };
  return (
    <button
      type={type}
      className="chat-button"
      onClick={onClick}
      disabled={disabled}
      style={{
        ...sizeStyle,
        borderRadius: "var(--chat-radius-md)",
        // Disabled CTA is the ghost state (DESIGN_CHEATSHEET "Buttons"): a
        // hairline keeps its shape, so it never reads as loose text.
        border: disabled ? "1px solid hsl(var(--foreground) / 0.2)" : "1px solid transparent",
        background: disabled ? "var(--chat-cta-disabled-bg)" : "var(--chat-cta-bg)",
        color: disabled ? "var(--chat-cta-disabled-fg)" : "var(--chat-cta-fg)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-md-size)",
        lineHeight: "var(--chat-type-body-md-line)",
        fontWeight: 500,
        cursor: disabled ? "default" : "pointer",
      }}
    >
      {children}
    </button>
  );
}

export function VerificationGhostButton({
  children,
  onClick,
  disabled,
  size = "sm",
  block = false,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  size?: VerificationButtonSize;
  /** Fill the container's width with a 44px target and centred label. */
  block?: boolean;
}) {
  const sizeStyle: CSSProperties = block
    ? { width: "100%", minHeight: 44, padding: "0 var(--chat-space-4)", textAlign: "center" }
    : size === "md"
      ? { minHeight: 24, padding: 0, whiteSpace: "nowrap" }
      : { height: 32, padding: "0 var(--chat-space-3)" };
  return (
    <button
      type="button"
      className="chat-button"
      onClick={onClick}
      disabled={disabled}
      style={{
        ...sizeStyle,
        borderRadius: "var(--chat-radius-sm)",
        border: "none",
        background: "transparent",
        color: disabled ? "var(--chat-fg-muted)" : "var(--chat-fg-secondary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-md-size)",
        lineHeight: "var(--chat-type-body-md-line)",
        fontWeight: 500,
        cursor: disabled ? "default" : "pointer",
      }}
    >
      {children}
    </button>
  );
}
